import { useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PlanType } from "src/apps/super-admin/tabs/plans/list/plan-types";
import Button from "src/components/buttons/Button";
import HorizontalSeparator from "src/components/seperators/HorizontalSeperator";
import useNotification from "src/hooks/useNotification";
import planBg2 from "src/images/pattern.svg";
import { getUserPlanRequest } from "src/redux/actions/app";
import { PLANS_FEATURES } from "src/utils/constants";
import { useAssignPlanToUser, useGetUserPlans } from "./api-client";

const SelectPlan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { successNotification, errorNotification } = useNotification();
  const { isLoading, data, error } = useGetUserPlans();
  const {
    isLoading: isPlanAssignLoading,
    execute: handlePlanAssignment,
    isSuccess: isPlanAssignAssignmentSuccess,
    error: isPlanAssignError,
  } = useAssignPlanToUser();

  const plansData = useMemo(() => data?.data ?? [], [data?.data]);

  useEffect(() => {
    if (isPlanAssignAssignmentSuccess) {
      dispatch(getUserPlanRequest());
      navigate("/select-app");
    }
  }, [dispatch, isPlanAssignAssignmentSuccess, navigate]);

  useEffect(() => {
    if (isPlanAssignError) {
      errorNotification();
    }
  }, [isPlanAssignError, errorNotification]);

  return (
    <div className="flex flex-col gap-20px">
      <div className="flex flex-col">
        <div className="font-semibold text-20px leading-8 text-blue-dark">
          Choose your Plan
        </div>
        <div className="text-14px font-normal text-grayishBlue">
          Choose the Plan that fits you best
        </div>
      </div>
      <div className="grid grid-cols-3 gap-[52px]">
        {isLoading || isPlanAssignLoading ? (
          Array(3)
            .fill(3)
            .map((item, index) => {
              return (
                <Skeleton key={index} height={"600px"} className="rounded-xl" />
              );
            })
        ) : (
          <>
            {plansData.map((item: PlanType) => {
              const { id, name, price, features } = item ?? {};
              return (
                <PlanCard
                  key={id}
                  title={name}
                  price={price}
                  features={features}
                  onPlanAssignment={() =>
                    handlePlanAssignment({ planId: id, price, planName: name })
                  }
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default SelectPlan;

const PlanCard = ({
  title,
  price,
  features,
  onPlanAssignment,
}: {
  title: string;
  price: number;
  features: string[];
  onPlanAssignment: () => void;
}) => {
  const filteredFeatures = useMemo(
    () =>
      PLANS_FEATURES.filter(({ value }) =>
        features.find((feature) => value === feature)
      ),
    [features]
  );

  return (
    <div className="flex flex-col bg-white text-center rounded-xl items-center relative p-[40px] overflow-clip">
      <div
        style={{
          backgroundImage: `url(${planBg2})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="w-full h-full absolute z-10"
      ></div>
      <div className="flex flex-col w-full z-20 items-center">
        <div className="flex flex-col gap-3 items-center text-center">
          <div className="font-bold text-[26px] text-black-raisin">{title}</div>
          <div className="font-normal text-16px text-gray-charcoal">
            Monthly Charge
          </div>
          <div className="font-bold text-5xl text-primary">{`₹${price}`}</div>
        </div>
        <HorizontalSeparator className="my-[30px]" />
        <div className="flex flex-col gap-20px">
          {filteredFeatures.map(
            ({ label, value }: { label: string; value: string }) => {
              return (
                <div key={value} className="font-semibold text-[18px]">
                  {label}
                </div>
              );
            }
          )}
        </div>
        <HorizontalSeparator className="my-[30px]" />
        <Button
          text={"Get Started"}
          className="!rounded-full px-6"
          primary
          onClick={onPlanAssignment}
        />
        <p className="mt-[30px] text-16px font-semibold underline cursor-pointer hover:text-primary">
          Start Your 30 Day Free Trial
        </p>
      </div>
    </div>
  );
};

// const PlanCard = () => {
//   return (
//     <div
//       style={{
//         backgroundImage: `url(${planBg2})`,
//         backgroundRepeat: "no-repeat",
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//       }}
//       className="rounded-xl overflow-hidden p-[40px]"
//     >
//       <div className="flex flex-col p-[40px] items-center text-center rounded-xl bg-white">
//         <div className="flex flex-col gap-3">
//           <div className="font-bold text-[26px] text-black-raisin">Basic</div>
//           <div className="font-normal text-16px text-gray-charcoal">
//             Monthly Charge
//           </div>
//           <div className="font-bold text-5xl text-primary">$14.99</div>
//         </div>
//         <HorizontalSeparator className="my-[30px]" />
//         <div className="flex flex-col gap-20px">
//           <div className="font-semibold text-[18px]">Free Setup</div>
//           <div className="font-semibold text-[18px]">Bandwidth Limit 10 GB</div>
//           <div className="font-semibold text-[18px]">20 User Connection</div>
//           <div className="font-semibold text-[18px]">Analytics Report</div>
//           <div className="font-semibold text-[18px]">Public API Access</div>
//           <div className="font-semibold text-[18px]">Plugins Integration</div>
//           <div className="font-semibold text-[18px]">
//             Custom Content Management
//           </div>
//         </div>
//         <HorizontalSeparator className="my-[30px]" />
//         <Button text={"Get Started"} className="!rounded-full px-6" primary />
//         <div className="mt-[30px] text-16px font-semibold underline">
//           Start Your 30 Day Free Trial
//         </div>
//       </div>
//     </div>
//   );
// };
